// Legacy submitted reports stored answers keyed by a slug derived from the field's
// label (since a field could never appear twice in a template). New templates key
// answers by each placement's instanceId instead, so the same field can appear
// more than once without colliding.
export const legacyKeyForField = (item) => item?.label?.toLowerCase()?.replace(/\s/g, "");

export const getFieldAnswer = (reportDetail, item) => {
    if (!reportDetail) return undefined;
    if (item?.instanceId !== undefined && reportDetail[item.instanceId] !== undefined) {
        return reportDetail[item.instanceId];
    }
    return reportDetail[legacyKeyForField(item)];
};

// Rebuilds the local `field` state array from a raw reportDetail object, remapping
// legacy label-slug keys onto the matching field's instanceId so downstream
// `field.find(f => f.name === item.instanceId)` lookups work uniformly for old and new data.
export const mapReportDetailToFieldState = (reportDetail, sections) => {
    if (!reportDetail) return [];
    const allItems = (sections || []).flatMap((s) => s?.fields || []);
    return Object.keys(reportDetail).map((key) => {
        const matched = allItems.find((i) => i.instanceId === key)
            || allItems.find((i) => legacyKeyForField(i) === key);
        // rawKey is the actual key this answer is stored under in reportDetail/filesURL —
        // needed by callers that also index a sibling filesURL object by the same key.
        return { name: matched ? matched.instanceId : key, rawKey: key, value: reportDetail[key] };
    });
};

// singleCheckbox answers aren't stored as real booleans, and the two platforms don't
// even agree on casing (web writes "yes"/"no", the mobile app writes "Yes"/"No") —
// normalize before comparing.
export const normalizeYesNo = (value) => value?.toString?.()?.toLowerCase?.();

// A field placement is visible unless it declares a condition on another placement's
// (the trigger's) answer. Conditions are stored on the dependent field itself, so this
// needs no knowledge of sections/siblings — just the live answers.
export const isFieldVisible = (item, fieldState) => {
    if (!item?.condition?.dependsOnInstanceId) return true;
    const trigger = fieldState?.find((f) => f.name === item.condition.dependsOnInstanceId);
    return normalizeYesNo(trigger?.value) === item.condition.equals;
};

// instanceIds of every field placement across all sections that is currently visible
// (its own condition, if any, is satisfied by the live answers). Used to drop hidden
// fields' stale answers before persisting a submission.
export const getVisibleInstanceIds = (sections, fieldState) => {
    const visible = new Set();
    (sections || []).forEach((section) => {
        (section?.fields || []).forEach((item) => {
            if (isFieldVisible(item, fieldState)) {
                visible.add(item.instanceId);
            }
        });
    });
    return visible;
};

// A value counts as "answered" only if it has real content — undefined/null, and
// whitespace-only strings (e.g. someone hitting the spacebar to get past a required
// field), all count as blank. Non-string values (numbers, File objects, arrays from a
// multi-checkbox) are left alone — trimming only makes sense for free-text input.
const isBlankAnswer = (value) => {
    if (value === undefined || value === null) return true;
    if (typeof value === 'string') return value.trim() === '';
    return false;
};

// Some report layouts (RelayOn, Ward Security) are hand-built HTML tables that read
// `reportDetail.sitename`, `reportDetail['isthereanyevidence...?']` etc. directly — hardcoded
// legacy label-slugs, never the field's instanceId, and they don't iterate `reportFields` at
// all so they can't be fixed the way the generic/template-driven renderers were. Rather than
// rewriting dozens of hardcoded lookups by hand (error-prone — the exact label text in the
// JSX has to match the DB's field label exactly), wrap `reportDetail` in a Proxy: any property
// read that isn't present as-is falls back to finding the template field whose own legacy slug
// matches the requested key, then reads that field's answer via its real instanceId. Existing
// submissions (legacy-slug-keyed) are untouched — this only fills the gap for new submissions.
export const wrapReportDetailForLegacyLookup = (reportDetail, templateFields) => {
    if (!reportDetail) return reportDetail;
    const bySlug = new Map();
    (templateFields || []).forEach((f) => {
        const slug = legacyKeyForField(f);
        if (slug && !bySlug.has(slug)) bySlug.set(slug, f);
    });
    return new Proxy(reportDetail, {
        get(target, prop, receiver) {
            if (typeof prop !== 'string') return Reflect.get(target, prop, receiver);
            if (target[prop] !== undefined) return target[prop];
            const field = bySlug.get(prop.toLowerCase());
            if (field?.instanceId !== undefined) return target[field.instanceId];
            return undefined;
        },
        has(target, prop) {
            if (typeof prop === 'string' && (target[prop] !== undefined || bySlug.has(prop.toLowerCase()))) {
                return true;
            }
            return Reflect.has(target, prop);
        },
    });
};

// Required placements (isMandatory) that are currently visible but have no answer yet.
export const getUnansweredRequiredFields = (sections, fieldState) => {
    const missing = [];
    (sections || []).forEach((section) => {
        (section?.fields || []).forEach((item) => {
            if (!item?.isMandatory) return;
            if (!isFieldVisible(item, fieldState)) return;
            const answer = fieldState?.find((f) => f.name === item.instanceId);
            if (isBlankAnswer(answer?.value)) {
                missing.push(item);
            }
        });
    });
    return missing;
};
